import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Youtube, ChevronUp, MapPin, Mail, Phone } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import BrandLogo from '../BrandLogo/BrandLogo';
import './Footer.css';

const Footer = () => {
  const { siteName, fullAddress, email, phone } = useSite();
  const [emailInput, setEmailInput] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    alert('Welcome to Deals List — check your inbox soon.');
    setEmailInput('');
  };

  const footerLinks = {
    shop: [
      { name: 'Women', href: '/category/women' },
      { name: 'Men', href: '/category/men' },
      { name: 'Activewear', href: '/category/activewear' },
      { name: 'Kids', href: '/category/kids' },
      { name: 'Home', href: '/category/home' },
      { name: 'Shoes', href: '/category/shoes' },
      { name: 'Bags', href: '/category/handbags' },
      { name: 'Accessories', href: '/category/accessories' },
      { name: 'Jewelry', href: '/category/jewelry' },
      { name: 'Beauty', href: '/category/beauty' },
    ],
    customerService: [
      { name: 'Contact Us', href: '/contact' },
      { name: 'FAQs', href: '/faqs' },
      { name: 'Shipping', href: '/shipping' },
      { name: 'Returns', href: '/returns' },
    ],
    about: [
      { name: `About ${siteName}`, href: '/about' },
      { name: 'Careers', href: '/careers' },
      { name: 'Press', href: '/press' },
    ],
    rewards: [
      { name: 'Fashion Deals Rewards', href: '/rewards' },
      { name: 'Gift Cards', href: '/gift-cards' },
      { name: 'Offers', href: '/coupons' },
    ],
  };

  const paymentMethods = ['VISA', 'MC', 'AMEX', 'DISC', 'PayPal'];

  return (
    <footer className="footer">
      <div className="footer-email-signup">
        <div className="footer-email-container">
          <div className="footer-email-text">
            <h3>Join Deals List</h3>
            <p>Flash deals, early access, and 15% off your first order.</p>
          </div>
          <form className="footer-email-form" onSubmit={handleEmailSubmit}>
            <input
              type="email"
              placeholder="Your email"
              className="footer-email-input"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              required
            />
            <button type="submit" className="footer-email-button">Subscribe</button>
          </form>
        </div>
      </div>

      <div className="footer-main">
        <div className="footer-grid">
          <div className="footer-column">
            <h4>Shop</h4>
            <ul className="footer-links">
              {footerLinks.shop.map((link) => (
                <li key={link.name}><Link to={link.href}>{link.name}</Link></li>
              ))}
            </ul>
          </div>
          <div className="footer-column">
            <h4>Help</h4>
            <ul className="footer-links">
              {footerLinks.customerService.map((link) => (
                <li key={link.name}><Link to={link.href}>{link.name}</Link></li>
              ))}
            </ul>
          </div>
          <div className="footer-column">
            <h4>Company</h4>
            <ul className="footer-links">
              {footerLinks.about.map((link) => (
                <li key={link.name}><Link to={link.href}>{link.name}</Link></li>
              ))}
            </ul>
          </div>
          <div className="footer-column">
            <h4>Rewards</h4>
            <ul className="footer-links">
              {footerLinks.rewards.map((link) => (
                <li key={link.name}><Link to={link.href}>{link.name}</Link></li>
              ))}
            </ul>
          </div>
          <div className="footer-column">
            <h4>Contact</h4>
            <div className="footer-contact">
              <p><MapPin size={14} /> {fullAddress}</p>
              <p><Mail size={14} /> <a href={`mailto:${email}`}>{email}</a></p>
              <p><Phone size={14} /> <a href={`tel:${phone}`}>{phone}</a></p>
            </div>
          </div>
          <div className="footer-column">
            <h4>Follow</h4>
            <div className="footer-social">
              <a href="https://instagram.com" className="footer-social-link" aria-label="Instagram"><Instagram size={18} /></a>
              <a href="https://twitter.com" className="footer-social-link" aria-label="Twitter"><Twitter size={18} /></a>
              <a href="https://facebook.com" className="footer-social-link" aria-label="Facebook"><Facebook size={18} /></a>
              <a href="https://youtube.com" className="footer-social-link" aria-label="YouTube"><Youtube size={18} /></a>
            </div>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="footer-bottom-content">
          <div className="footer-brand-block">
            <Link to="/" className="footer-brand" aria-label={siteName}>
              <BrandLogo size={28} />
            </Link>
            <p className="footer-copyright">© {new Date().getFullYear()} {siteName}. All rights reserved.</p>
          </div>
          <div className="footer-legal">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy#do-not-sell">Do Not Sell My Info</Link>
          </div>
        </div>
        <div className="footer-payments">
          <span className="footer-payments-label">We accept</span>
          {paymentMethods.map((method) => (
            <span key={method} className="footer-payment-icon">{method}</span>
          ))}
        </div>
      </div>

      <button
        type="button"
        className={`footer-back-top ${showBackToTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <ChevronUp size={22} />
      </button>
    </footer>
  );
};

export default Footer;
